/**
 * Game vocabulary shared across components.
 *
 * These types mirror the GDD entity catalogue. Keeping them here means a
 * resource's colour, label and icon are decided in exactly one place — add a
 * resource once and every readout, cost list and tooltip picks it up.
 */

/** The seven tracked economy entities (GDD §1). */
export type ResourceKey =
  | 'titan'
  | 'silicate'
  | 'isotope'
  | 'power'
  | 'aether'
  | 'credit'
  | 'chronos';

export interface ResourceMeta {
  key: ResourceKey;
  /** German in-game name, as used in the UI. */
  label: string;
  /** Short form for dense readouts. */
  short: string;
  /** Tailwind accent family — drives text-*, bg-*, border-* and shadow-glow-*. */
  accent: ResourceKey;
  /** Economic role, drives grouping in the resource bar. */
  kind: 'base' | 'infrastructure' | 'endgame' | 'currency';
}

export const RESOURCES: Record<ResourceKey, ResourceMeta> = {
  titan: {
    key: 'titan',
    label: 'Titan-Legierung',
    short: 'Titan',
    accent: 'titan',
    kind: 'base',
  },
  silicate: {
    key: 'silicate',
    label: 'Kristall-Silikat',
    short: 'Silikat',
    accent: 'silicate',
    kind: 'base',
  },
  isotope: {
    key: 'isotope',
    label: 'Isotop-3',
    short: 'Isotop',
    accent: 'isotope',
    kind: 'base',
  },
  power: {
    key: 'power',
    label: 'Megawatt',
    short: 'Energie',
    accent: 'power',
    kind: 'infrastructure',
  },
  aether: {
    key: 'aether',
    label: 'Aether-Masse',
    short: 'Aether',
    accent: 'aether',
    kind: 'endgame',
  },
  credit: {
    key: 'credit',
    label: 'Galaktische Credits',
    short: 'Credits',
    accent: 'credit',
    kind: 'currency',
  },
  chronos: {
    key: 'chronos',
    label: 'Chronos-Kristalle',
    short: 'Chronos',
    accent: 'chronos',
    kind: 'currency',
  },
};

export const RESOURCE_ORDER: ResourceKey[] = [
  'titan',
  'silicate',
  'isotope',
  'power',
  'aether',
  'credit',
  'chronos',
];

/** A cost or payout, keyed by resource. Partial — most costs use 2–3 resources. */
export type ResourceAmounts = Partial<Record<ResourceKey, number>>;

/* ------------------------------------------------------------------ *
 * Galactic ring system (GDD §3) — five concentric matchmaking zones.  *
 * Ring 5 is the outer newbie cradle, Ring 1 the endgame core.         *
 * ------------------------------------------------------------------ */

export { RINGS, RING_ORDER, type RingId, type RingMeta } from '@spacegame/engine';

/* ------------------------------------------------------------------ *
 * Shared state vocabularies                                           *
 * ------------------------------------------------------------------ */

/** Lifecycle of anything buildable or researchable. */
export type ProgressState =
  | 'locked' // prerequisites unmet
  | 'available' // can be started now
  | 'insufficient' // unlocked, but resources missing
  | 'active' // currently building / researching
  | 'damaged' // hit by a comet, needs repair (GDD §3 event system)
  | 'maxed'; // at maximum level

/** Fleet mission types (GDD §5 step 4). */
export type MissionType =
  | 'attack'
  | 'transport'
  | 'spy'
  | 'harvest'
  | 'colonize'
  | 'deploy'
  | 'recall';

export const MISSION_LABELS: Record<MissionType, string> = {
  attack: 'Angriff',
  transport: 'Transport',
  spy: 'Spionage',
  harvest: 'Abbau',
  colonize: 'Kolonisierung',
  deploy: 'Stationierung',
  recall: 'Rückflug',
};

/** Ship and defence base stats, in GDD order (hull / shield / damage / cargo). */
export interface CombatStats {
  hull: number;
  shield: number;
  damage: number;
  /** Defence structures have no cargo hold. */
  cargo?: number;
}

/** Severity ladder shared by alerts, toasts and the event feed. */
export type Severity = 'info' | 'success' | 'warning' | 'danger';
